import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { usePopper } from 'react-popper';
import ReactDOM from 'react-dom';

/**
 * Translates component 'placement' prop to string expected by Popper component
 * for placement parameter.
 *   Example: 'bottom-left' is used as 'bottom-start' by Popper.
 * @param {String} propPlacement - Component 'placement' prop
 * @returns {String}
 */
export const getPopperPlacement = propPlacement => {
  const arr = propPlacement.split('-');
  switch (arr[1]) {
    case 'left':
      return `${arr[0]}-start`;
    case 'right':
      return `${arr[0]}-end`;
    default:
      return propPlacement;
  }
};

/**
 * @typedef Props
 * @prop  {React.ReactNode} children
 * @prop  {string} [className]
 * @prop {boolean} [open]
 * @prop {'bottom-left' | 'bottom-right'} [placement]
 * @prop {React.ReactNode} trigger
 * @prop {string} [id]
 * @prop {() => void} [handleClickOutside]
 * @prop {string} [portalClassName]
 * @prop {string} [ariaLabelledby]
 * @prop {() => void} [onForceUpdateCreated]
 * @prop {boolean} [hideArrow]
 */
/**
 * @param {Props & {[x: string]: any;}} props
 */
const MotifDropdownPortal = props => {
  const {
    children,
    open = false,
    className = '',
    placement = 'bottom-right',
    trigger = null,
    id = null,
    handleClickOutside = () => null,
    ariaLabelledby = null,
    portalClassName = '',
    onForceUpdateCreated: handleForceUpdateCreated = null,
    hideArrow = false,
    ...other
  } = props;

  const [openDropdown, setOpenDropdown] = useState(false);
  const [currentItem, setCurrentItem] = useState(-1);
  const triggerRef = useRef();

  /**
   * Reference to DOM component
   */
  const refTarget = useRef(null);

  /**
   * Reference to popper DOM floating DIV, shown when user clicks trigger
   */
  const refPopper = useRef(null);

  /**
   * Reference to DOM trigger button
   */
  const refTrigger = useRef(null);

  /**
   * Reference to DOM arrow
   */
  const refArrow = useRef(null);

  /**
   * Popper library hook returns 'styles' and 'attributes' objects that should
   * be applied directly to popper element
   */
  const { styles, attributes, forceUpdate } = usePopper(refTrigger.current, refPopper.current, {
    placement: getPopperPlacement(placement),
    modifiers: [
      // Modifier provides arrow CSS inside styles.arrow
      { name: 'arrow', options: { element: refArrow.current } },

      // Modifier allows to offset tip of the arrow distance to trigger
      { name: 'offset', options: { offset: hideArrow ? [] : [0, 8] } },

      // Modifier allows to hide open popper if trigger is not visible. This
      // happens if popper is open and user scrolls container until trigger
      // is hidden
      { name: 'hide' }
    ]
  });

  const itemsChildren = React.Children.map(children, (child, index) =>
    React.cloneElement(child, {
      indexItem: index,
      currentItem
    })
  );

  const triggerButton = React.Children.map(trigger, (child, index) =>
    React.cloneElement(child, {
      ref: triggerRef,
      'aria-expanded': openDropdown,
      'aria-haspopup': 'true',
      'aria-controls': id
    })
  );

  useEffect(() => {
    setOpenDropdown(open);
  }, [open]);

  /**
   * Listen click events after component first render. Used to detect if user
   * clicked outside an open popper to close it.
   */
  useEffect(() => {
    document.addEventListener('click', clickOutside);

    return () => document.removeEventListener('click', clickOutside);
  }, []);

  useEffect(() => {
    if (forceUpdate && handleForceUpdateCreated) {
      handleForceUpdateCreated(forceUpdate);
    }
  }, [forceUpdate, handleForceUpdateCreated]);

  /**
   * Listener executes this method to veify if user clicked outside component.
   * Used to close popper if opened.
   * @param {Object} event - Click event
   */
  const clickOutside = event => {
    if (!event || !event.target) {
      return;
    }
    if (refPopper && refPopper.current && refPopper.current.contains(event.target)) {
      return;
    }
    if (refTarget && refTarget.current && !refTarget.current.contains(event.target)) {
      handleClickOutside(id);
    }
  };

  const handleKeyDown = event => {
    const pressedKey = event.key;

    switch (pressedKey) {
      case 'ArrowUp':
        event.preventDefault();
        if (currentItem > 0) {
          setCurrentItem(currentItem - 1);
        } else {
          setCurrentItem(itemsChildren.length - 1);
        }
        break;

      case 'ArrowDown':
        event.preventDefault();
        if (currentItem < itemsChildren.length - 1) {
          setCurrentItem(currentItem + 1);
        } else {
          setCurrentItem(0);
        }
        break;

      case 'Escape':
        event.preventDefault();
        setOpenDropdown(false);
        if (triggerRef.current) {
          triggerRef.current.focus();
        }

        break;

      case 'Enter':
        event.preventDefault();
        setOpenDropdown(true);
        break;

      case 'Tab':
        setOpenDropdown(false);
        if (triggerRef.current) {
          triggerRef.current.focus();
        }
        break;

      default:
        break;
    }
  };

  /**
   * Variable to set all classes in component
   */
  const classes = classNames({
    'motif-dropdown-portal': true,
    [className]: true
  });

  return (
    <div
      onKeyDown={handleKeyDown}
      className={classes}
      {...other}
      ref={refTarget}
      data-motif-dropdown-portal
    >
      <div className="motif-dropdown-trigger" ref={refTrigger}>
        {triggerButton}
      </div>
      {ReactDOM.createPortal(
        <div
          className={`motif-dropdown-menu-portal ${
            openDropdown ? '' : 'motif-dropdown-menu-portal-close'
          } ${portalClassName} `}
          style={styles.popper}
          id={id}
          role="menu"
          tabIndex="0"
          aria-labelledby={ariaLabelledby}
          ref={refPopper}
          {...attributes.popper}
        >
          {itemsChildren}
          {hideArrow || (
            <div
              ref={refArrow}
              style={styles.arrow}
              className="motif-dropdown-arrow"
              data-popper-arrow
            />
          )}
        </div>,
        document.querySelector('body')
      )}
    </div>
  );
};

MotifDropdownPortal.propTypes = {
  children: PropTypes.node,
  trigger: PropTypes.node,
  className: PropTypes.string,
  id: PropTypes.string,
  ariaLabelledby: PropTypes.string,
  handleClickOutside: PropTypes.func,
  onForceUpdateCreated: PropTypes.func,
  open: PropTypes.bool,
  placement: PropTypes.oneOf(['bottom-left', 'bottom-right']),
  portalClassName: PropTypes.string,
  hideArrow: PropTypes.bool
};

export { MotifDropdownPortal };
